import { colord, extend } from "colord";
import a11yPlugin from "colord/plugins/a11y";
import { type RampStop, generateRamp } from "./generate-ramp";

extend([a11yPlugin]);

const BLACK_RGB = "0, 0, 0";
const WHITE_RGB = "255, 255, 255";

type Ramp = Record<RampStop, string>;
type RampRole = "primary" | "accent";

const onColorFor = (rgbTriple: string) => (colord(`rgb(${rgbTriple})`).isDark() ? WHITE_RGB : BLACK_RGB);

const buildCssVariables = (ramp: Ramp, type: RampRole): string => {
	if (type === "primary") {
		const onColor = onColorFor(ramp[600]);
		return [
			`--os-color-primary-100: ${ramp[100]};`,
			`--os-color-primary-200: ${ramp[200]};`,
			`--os-color-primary-300: ${ramp[300]};`,
			`--os-color-primary-400: ${ramp[400]};`,
			`--os-color-primary-500: ${ramp[500]};`,
			`--os-color-primary-600: ${ramp[600]};`,
			`--os-color-primary-700: ${ramp[700]};`,
			`--os-color-primary-800: ${ramp[800]};`,
			`--os-color-primary-900: ${ramp[900]};`,
			"--os-color-bg-primary: var(--os-color-primary-600);",
			"--os-color-bg-primary-strong: var(--os-color-primary-700);",
			"--os-color-bg-primary-faded: var(--os-color-primary-100);",
			"--os-color-fg-primary: var(--os-color-primary-600);",
			"--os-color-fg-primary-strong: var(--os-color-primary-700);",
			"--os-color-fg-primary-faded: var(--os-color-primary-500);",
			"--os-color-border-primary: var(--os-color-primary-500);",
			"--os-color-border-primary-strong: var(--os-color-primary-700);",
			"--os-color-border-primary-faded: var(--os-color-primary-200);",
			`--os-color-fg-on-primary: ${onColor};`,
			"--os-color-on-bg-primary: var(--os-color-fg-on-primary);",
		].join(" ");
	}

	const onColor = onColorFor(ramp[600]);
	return [
		`--os-color-accent-100: ${ramp[100]};`,
		`--os-color-accent-150: ${ramp[150]};`,
		`--os-color-accent-200: ${ramp[200]};`,
		`--os-color-accent-300: ${ramp[300]};`,
		`--os-color-accent-400: ${ramp[400]};`,
		`--os-color-accent-500: ${ramp[500]};`,
		`--os-color-accent-600: ${ramp[600]};`,
		`--os-color-accent-700: ${ramp[700]};`,
		`--os-color-accent-800: ${ramp[800]};`,
		`--os-color-accent-900: ${ramp[900]};`,
		"--os-color-bg-accent: var(--os-color-accent-500);",
		"--os-color-bg-accent-strong: var(--os-color-accent-700);",
		"--os-color-bg-accent-faded: var(--os-color-accent-100);",
		"--os-color-fg-accent: var(--os-color-accent-600);",
		"--os-color-fg-accent-strong: var(--os-color-accent-700);",
		"--os-color-fg-accent-faded: var(--os-color-accent-300);",
		"--os-color-border-accent: var(--os-color-accent-500);",
		"--os-color-border-accent-strong: var(--os-color-accent-700);",
		"--os-color-border-accent-faded: var(--os-color-accent-200);",
		`--os-color-fg-on-accent: ${onColor};`,
		"--os-color-on-bg-accent: var(--os-color-fg-on-accent);",
	].join(" ");
};

export const generateColorVariables = (hex: string, type: RampRole) => buildCssVariables(generateRamp(hex, type), type);

export const generateColorVariablesFromRamp = (ramp: Ramp, type: RampRole) => buildCssVariables(ramp, type);
